"use client";

import Link from "next/link";
import { FormEvent, useState } from "react";

const products = ["LITKHATA", "LITCRM", "LITFusion", "LITWay"];

export default function DeleteAccountForm() {
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [notice, setNotice] = useState("");

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const payload = {
      product: String(data.get("product") || ""),
      userId: String(data.get("userId") || ""),
      password: String(data.get("password") || ""),
      reason: String(data.get("reason") || ""),
      website: String(data.get("website") || ""),
    };
    setStatus("sending");
    setNotice("");

    const response = await fetch("/api/delete-account", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).catch(() => null);
    const result = await response?.json().catch(() => null);

    if (response?.ok && result?.ok) {
      form.reset();
      setStatus("sent");
      return;
    }
    setStatus("error");
    if (result?.reason === "rate_limited") {
      setNotice("Please wait a little while before sending another request, or call us.");
      return;
    }
    if (result?.reason === "invalid") {
      setNotice("Choose a product, then enter your user ID, password, and a short reason.");
      return;
    }
    setNotice("The request could not be sent. Please email sales@litpl.com or call +91 99070 36906. Do not send your password in an email.");
  }

  if (status === "sent") {
    return (
      <div className="form-shell p-6 sm:p-8" role="status">
        <h2 className="text-2xl font-bold text-slate-900">Request received</h2>
        <p className="mt-3 text-slate-600">We will check the user ID against the product you selected and confirm before the account is deleted.</p>
        <Link href="/" className="primary-action mt-6">Back to home</Link>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="form-shell p-6 sm:p-8" aria-label="Delete account request">
      <label className="block text-sm font-semibold text-slate-800">
        Product
        <select name="product" required defaultValue="" className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-3 py-3">
          <option value="" disabled>Select a product</option>
          {products.map((name) => (
            <option key={name} value={name}>{name}</option>
          ))}
        </select>
      </label>
      <label className="mt-4 block text-sm font-semibold text-slate-800">
        User ID
        <input name="userId" required autoComplete="username" className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-3" placeholder="The ID you use to sign in" />
      </label>
      <label className="mt-4 block text-sm font-semibold text-slate-800">
        Password
        <input name="password" type="password" required autoComplete="current-password" className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-3" placeholder="Your sign-in password" />
      </label>
      <label className="mt-4 block text-sm font-semibold text-slate-800">
        Reason
        <textarea name="reason" required minLength={10} rows={4} className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-3" placeholder="Tell us why you want to delete this account" />
      </label>
      <div className="hidden" aria-hidden="true">
        <input name="website" tabIndex={-1} autoComplete="off" />
      </div>
      {notice && <p className="mt-4 text-sm text-red-700">{notice}</p>}
      <button type="submit" disabled={status === "sending"} className="primary-action mt-6 w-full disabled:opacity-60">
        {status === "sending" ? "Sending…" : "Request deletion"}
      </button>
      <p className="mt-4 text-sm text-slate-500">
        We use the password only to confirm this request, then delete the account for the product you selected. Read the <Link href="/privacy-policy" className="underline underline-offset-4">privacy policy</Link>.
      </p>
    </form>
  );
}
